import { apiClient } from './client'
import { ApiError } from './errors'

// The serverless backend uses snake_case request/response fields and there is NO
// snake<->camel transform in the axios layer (it only unwraps the envelope). So
// each function below sends the exact BE body and maps the snake-case response
// onto the camelCase shape the components/hooks already consume.

function appReturnUrl(path: string): string {
  // Absolute URL POLi/BlinkPay redirect the browser back to after payment.
  const origin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : ''
  return `${origin}${path}`
}

export async function createPoliDirectDebit(data: {
  amount: number
  productId: number
}): Promise<{ navigationUrl: string }> {
  try {
    // BE PoliDirectDebitIn: { amount, merchant_reference, success_url, failure_url }.
    // Response PoliDirectDebitOut: { navigation_url } (snake).
    const response = await apiClient.post<{ navigation_url?: string }>(
      'payments/poli/direct_debit',
      {
        amount: data.amount,
        merchant_reference: `topup-${Date.now()}`,
        success_url: appReturnUrl('/wallet/POLi_transfer/loading'),
        failure_url: appReturnUrl('/wallet/POLi_transfer/status')
      }
    )
    return { navigationUrl: response.data?.navigation_url ?? '' }
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function verifyPoliTransaction(token: string): Promise<{
  success: boolean
  message: string
  transactionStatus: string
}> {
  try {
    // BE PoliVerifyIn: POST body { token }. Response: { success, message,
    // transaction_status } (snake).
    const response = await apiClient.post<{
      success?: boolean
      message?: string
      transaction_status?: string
    }>('payments/poli/verify', { token })
    return {
      success: response.data?.success ?? false,
      message: response.data?.message ?? '',
      transactionStatus: response.data?.transaction_status ?? ''
    }
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function createBlinkpayDirectDebit(data: {
  amount: number
  productId: number
}): Promise<{ navigationUrl: string; quickPaymentId: string }> {
  try {
    // BE BlinkpayDirectDebitIn: { amount, particulars, redirect_uri }.
    // Response BlinkpayDirectDebitOut: { navigation_url, quick_payment_id } (snake).
    const response = await apiClient.post<{
      navigation_url?: string
      quick_payment_id?: string
    }>('payments/blinkpay/direct_debit', {
      amount: data.amount,
      particulars: 'Goldie top-up',
      redirect_uri: appReturnUrl('/wallet/BlinkPay/loading')
    })
    return {
      navigationUrl: response.data?.navigation_url ?? '',
      quickPaymentId: response.data?.quick_payment_id ?? ''
    }
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function verifyBlinkpayTransaction(
  quickPaymentId: string
): Promise<{ success: boolean; message: string; transactionStatus: string }> {
  try {
    // BE BlinkpayVerifyIn: POST body { quick_payment_id }. Response: { success,
    // message, transaction_status } (snake).
    const response = await apiClient.post<{
      success?: boolean
      message?: string
      transaction_status?: string
    }>('payments/blinkpay/verify', { quick_payment_id: quickPaymentId })
    return {
      success: response.data?.success ?? false,
      message: response.data?.message ?? '',
      transactionStatus: response.data?.transaction_status ?? ''
    }
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function verifyStripeTransaction(
  paymentIntentId: string
): Promise<{ success: boolean; transactionStatus: string }> {
  try {
    // BE StripeVerifyIn: POST body { payment_intent_id }. Response StripeVerifyOut:
    // { success, transaction_status } (snake).
    const response = await apiClient.post<{
      success?: boolean
      transaction_status?: string
    }>('payments/stripe/verify', {
      payment_intent_id: paymentIntentId
    })
    return {
      success: response.data?.success ?? false,
      transactionStatus: response.data?.transaction_status ?? ''
    }
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}
